import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Input from "../../../components/ui/common/Input";
import Select from "../../ui/select/Select";
import Button from "../../ui/common/Button";
import ToggleSwitch from "../../../components/ui/toggleSwitch/ToggleSwitch";
import { SettingsIcon } from "../../../icons/Icons";
import { FEATURES } from "./Features";
import CopyConfiguration from "./CopyConfiguration";
import Modal from "../../ui/modal/Modal";
import StatisticsSettings from "./StatisticsSettings";
import AiModal from "./submodals/AiModal";
import WavesModal from "./submodals/WavesModal";
import InsightsSettingsModal from "./submodals/InsightsSettingsModal";
import ReadingTimeSettingsModal from "./submodals/ReadingTimeSettingsModal";
import StoriesSettingsModal from "./submodals/StoriesSettingsModal";
import TentaclesSettingsModal from "./submodals/TentaclesSettingsModal";
import CustomMetricsModal from "./submodals/CustomMetricsModal";
import VideosModal from "./submodals/VideosModal";
import ChannelOptimalisationModal from "./submodals/ChannelOptimalisationModal";
import { createBrand, updateBrand, resetBrandStates } from "../../../store/slices/brands";
import { createCredits, updateCredits, deleteCredits } from "../../../store/slices/credits";
import { timezones } from "../../../constants/timezones";

import { useAppDispatch } from "../../../store";
import "./EditBrandModal.scss";
import "./submodals/SubModalGeneral.scss";

type EditBrandModalProps = {
  brand: any;
  newBrand: boolean;
  onClose: Function;
  brands: any;
};

const EditBrandModal = ({ brand, newBrand, onClose, brands }: EditBrandModalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [showCopyConfiguration, setShowCopyConfiguration] = useState(false);
  const [showStatisticsSettings, setShowStatisticsSettings] = useState(false);
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [activeSubModal, setActiveSubModal] = useState("");
  const [hasAttemptedUpdate, setHasAttemptedUpdate] = useState(false);

  const [brandActive, setBrandActive] = useState(brand?.active?.value || false);
  const [brandFeatures, setBrandFeatures] = useState(brand?.features?.value || []);

  const [brandId, setBrandId] = useState(brand?.id?.value);
  const [brandName, setBrandName] = useState(brand?.name?.value);
  const [timezone, setTimezone] = useState(brand?.timezone?.value);
  const [readingDirection] = useState(brand?.readingDirection?.value || brand?.readingDirection?.default);
  const [trackingDays, setTrackingDays] = useState(brand?.trackingDays?.value || brand?.trackingDays?.default);
  const [primaryStoryType, setPrimaryStoryType] = useState(
    brand?.primaryStoryType?.value || brand?.primaryStoryType?.default,
  );

  const [aiSettings, setAiSettings] = useState(brand.aiSettings);
  const [insightsSettings, setInsightsSettings] = useState(brand.insightsSettings);
  const [readingTimeSettings, setReadingTimeSettings] = useState(brand.readingTimeSettings);
  const [statisticsSettings, setStatisticsSettings] = useState(brand.statisticsSettings);
  const [storiesSettings, setStoriesSettings] = useState(brand.storiesSettings);
  const [tentaclesSettings, setTentaclesSettings] = useState(brand.tentaclesSettings);
  const [videosSettings, setVideosSettings] = useState(brand.videosSettings);
  const [wavesSettings, setWavesSettings] = useState(brand.wavesSettings);
  const [customMetricsSettings, setCustomMetricsSettings] = useState(brand?.customMetricsSettings?.value || brand?.customMetricsSettings?.default);

  const [brandLogoSrc, setBrandLogoSrc] = useState(brand.brandlogo?.value || brand.brandlogo?.default);

  const creatingBrand = useSelector((state: any) => state.brands.creatingBrand);
  const creatingBrandError = useSelector((state: any) => state.brands.creatingBrandError);

  const updatingBrand = useSelector((state: any) => state.brands.updatingBrand);
  const updatingBrandError = useSelector((state: any) => state.brands.updatingBrandError);

  const { subModalId, endpointGoal } = useParams();
  const validSubModals = useMemo(() => ["insights", "stories", "readingtime", "tentacles", "videos", "waves", "ai", "custommetrics", "channeloptimalisation"], []);

  useEffect(() => {
    if (subModalId && validSubModals.includes(subModalId)) {
      setActiveSubModal(subModalId);
      setSubModalOpen(true);
    }
  }, [subModalId, validSubModals]);

  useEffect(() => {
    // Initialize and sync all three settings objects when the component mounts or when any of them changes
    
    // Get values from tentaclesSettings first (as the source of truth)
    const tentaclesMainContent = tentaclesSettings.settings.mainContent?.value || tentaclesSettings.settings.mainContent?.default || "";
    const tentaclesExcludeContent = tentaclesSettings.settings.excludeContent?.value || tentaclesSettings.settings.excludeContent?.default || "";
    
    // Sync readingTimeSettings with tentaclesSettings if needed
    const shouldUpdateReadingTimeSettings = 
      !readingTimeSettings.mainContent?.value !== undefined || 
      readingTimeSettings.mainContent?.value !== tentaclesMainContent ||
      !readingTimeSettings.excludeContent?.value !== undefined || 
      readingTimeSettings.excludeContent?.value !== tentaclesExcludeContent

    if (shouldUpdateReadingTimeSettings) {
      setReadingTimeSettings(prev => ({
        ...prev,
        mainContent: {
          default: tentaclesSettings.settings.mainContent?.default || "",
          value: tentaclesMainContent
        },
        excludeContent: {
          default: tentaclesSettings.settings.excludeContent?.default || "",
          value: tentaclesExcludeContent
        }
      }));
    }
    
    // Sync aiSettings mainContent and excludeContent with tentaclesSettings if needed
    const shouldUpdateAiSettings = 
      !aiSettings.mainContent?.value !== undefined || 
      aiSettings.mainContent?.value !== tentaclesMainContent ||
      !aiSettings.excludeContent?.value !== undefined || 
      aiSettings.excludeContent?.value !== tentaclesExcludeContent;
      
    if (shouldUpdateAiSettings) {
      setAiSettings(prev => ({
        ...prev,
        mainContent: {
          default: tentaclesSettings.settings.mainContent?.default || "",
          value: tentaclesMainContent
        },
        excludeContent: {
          default: tentaclesSettings.settings.excludeContent?.default || "",
          value: tentaclesExcludeContent
        }
      }));
    }
  }, [tentaclesSettings, aiSettings.excludeContent?.value, aiSettings.mainContent?.value, readingTimeSettings.excludeContent?.value, readingTimeSettings.mainContent?.value]);

  const handleToggleCopyConfiguration = () => {
    if (showCopyConfiguration) {
      setShowCopyConfiguration(false);
    } else {
      setShowCopyConfiguration(true);
      setShowStatisticsSettings(false);
    }
  };

  const handleToggleStatistics = () => {
    if (showStatisticsSettings) {
      setShowStatisticsSettings(false);
    } else {
      setShowStatisticsSettings(true);
      setShowCopyConfiguration(false);
    }
  };

  const handleSaveAiSettings = (data) => {
    // Update the aiSettings state
    setAiSettings(data);
    
    // Also update the corresponding values in tentaclesSettings
    setTentaclesSettings(prevSettings => ({
      ...prevSettings,
      settings: {
        ...prevSettings.settings,
        mainContent: {
          ...prevSettings.mainContent,
          value: data.mainContent?.value || prevSettings.mainContent?.value
        },
        excludeContent: {
          ...prevSettings.excludeContent,
          value: data.excludeContent?.value || prevSettings.excludeContent?.value
        }
      }
    }));
    
    // Also update the corresponding values in readingTimeSettings
    setReadingTimeSettings(prevSettings => ({
      ...prevSettings,
      mainContent: {
        ...prevSettings.mainContent,
        value: data.mainContent?.value || prevSettings.mainContent?.value
      },
      excludeContent: {
        ...prevSettings.excludeContent,
        value: data.excludeContent?.value || prevSettings.excludeContent?.value
      }
    }));
  };

  const handleSaveAiCredits = useCallback((data) => {
    if (data.id === "") {
      dispatch(createCredits(brandId, data));
    } else {
      dispatch(updateCredits(brandId, data.id, data));
    }
  }, [dispatch, brandId]);

  const handleDeleteAiCredits = useCallback((creditId) => {
    dispatch(deleteCredits(brandId, creditId));
  }, [dispatch, brandId]);

  const handleSaveInsightsSettings = (data) => {
    setInsightsSettings(data);
  };

  const handleSaveStoriesSettings = (data) => {
    setStoriesSettings(data);
  };

  const handleSaveTentaclesSettings = (data) => {
    setTentaclesSettings(data);
  };

  const handleSaveReadingTimeSettings = (data) => {
    // Update the readingTimeSettings state
    setReadingTimeSettings(data);
    
    // Also update the corresponding values in tentaclesSettings
    setTentaclesSettings(prevSettings => ({
      ...prevSettings,
      settings: {
        ...prevSettings.settings,
        mainContent: {
          ...prevSettings.mainContent,
          value: data.mainContent.value
        },
        excludeContent: {
          ...prevSettings.excludeContent,
          value: data.excludeContent.value
        }
      }
    }));
    
    // Also update the corresponding values in aiSettings (excluding collectReadingtime)
    setAiSettings(prevSettings => ({
      ...prevSettings,
      mainContent: {
        ...prevSettings.mainContent,
        value: data.mainContent.value
      },
      excludeContent: {
        ...prevSettings.excludeContent,
        value: data.excludeContent.value
      }
    }));
  };

  const handleSaveVideosSettings = (data) => {
    setVideosSettings(data);
  };

  const handleSaveWavesSettings = (data) => {
    setWavesSettings(data);
  };

  const handleCustomMetricSetings = (data) => {
    setCustomMetricsSettings(data);
  };

  const handleEditStaticsSettings = (input, parent, field) => {
    setStatisticsSettings({
      ...statisticsSettings,
      [parent]: {
        ...statisticsSettings[parent],
        [field]: { default: statisticsSettings[parent][field]["default"], value: input },
      },
    });
  };

  const closeSubModal = useCallback(() => {
    setActiveSubModal("");
    setSubModalOpen(false);
    navigate(`/admin/brands/${brandId}/`);
  }, [navigate, brandId]);

  const closeModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const currentSubModal = useMemo(() => {
    switch (activeSubModal) {
      case "insights":
        return (
          <InsightsSettingsModal data={insightsSettings} onSave={handleSaveInsightsSettings} onClose={closeSubModal} />
        );
      case "stories":
        return (
          <StoriesSettingsModal data={storiesSettings} onSave={handleSaveStoriesSettings} onClose={closeSubModal} />
        );
      case "tentacles":
        return (
          <TentaclesSettingsModal
            data={tentaclesSettings}
            brandId={brandId}
            onSave={handleSaveTentaclesSettings}
            onClose={closeSubModal}
          />
        );
        case "readingtime":
        return (
          <ReadingTimeSettingsModal
            data={readingTimeSettings}
            onSave={handleSaveReadingTimeSettings}
            onClose={closeSubModal}
          />
        );
      case "videos":
        return <VideosModal data={videosSettings} onSave={handleSaveVideosSettings} onClose={closeSubModal} />;
      case "waves":
        return <WavesModal data={wavesSettings} onSave={handleSaveWavesSettings} onClose={closeSubModal} />;
      case "ai":
        return (
          <AiModal
            data={aiSettings}
            brandName={brandName}
            brandId={brandId}
            endpointGoal={endpointGoal}
            onSave={handleSaveAiSettings}
            onSaveCredits={handleSaveAiCredits}
            onDeleteCredits={handleDeleteAiCredits}
            onClose={closeSubModal}
          />
        );
      case "custommetrics":
        return (
          <CustomMetricsModal data={customMetricsSettings} onSave={handleCustomMetricSetings} onClose={closeSubModal}/>
        );
      case "channeloptimalisation":
        return (
          <ChannelOptimalisationModal
            brandId={brandId}
            brandName={brandName}
            onClose={closeSubModal}
          />
        );
      default:
        return <div></div>;
    }
  }, [activeSubModal, aiSettings, brandId, brandName, closeSubModal, customMetricsSettings, handleDeleteAiCredits, handleSaveAiCredits, insightsSettings, readingTimeSettings, storiesSettings, tentaclesSettings, videosSettings, wavesSettings]);

  const handleFeatureSwitch = useCallback(
    (clickedFeature) => {
      if (brandFeatures.includes(clickedFeature)) {
        setBrandFeatures(
          brandFeatures.filter((feature) => {
            return feature !== clickedFeature;
          }),
        );
        
        // If readingtime feature is being removed, set collectReadingtime to false
        if (clickedFeature === "readingtime") {
          // Update tentaclesSettings
          setTentaclesSettings(prevSettings => ({
            ...prevSettings,
            settings: {
              ...prevSettings.settings,
              collectReadingtime: {
                ...prevSettings.settings.collectReadingtime,
                value: false
              }
            }
          }));
        }
      } else {
        setBrandFeatures([...brandFeatures, clickedFeature]);
        
        // If readingtime feature is being added, set collectReadingtime to true
        if (clickedFeature === "readingtime") {
          // Update tentaclesSettings
          setTentaclesSettings(prevSettings => ({
            ...prevSettings,
            settings: {
              ...prevSettings.settings,
              collectReadingtime: {
                ...prevSettings.settings.collectReadingtime,
                value: true
              }
            }
          }));
        }
      }
    },
    [brandFeatures],
  );

  const handleFileUpdate = (e) => {
    // @ts-ignore
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setBrandLogoSrc(reader.result);
    });

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const mapValuesToBaseAttr = (objectToMap) => {
    const newObject = { ...objectToMap };
    for (const key in newObject) {
      if (Object.prototype.hasOwnProperty.call(newObject, key)) {
        // Check if the 'default' property of the current 'newObject' key is of type boolean
        if (typeof newObject[key].default === "boolean") {
          // If true, assign the 'value' property to 'newObject[key]' because the switch was toggled to the state of the value else the default
          newObject[key] = newObject[key].value === true;
        } else {
          // If the 'default' property is not a boolean, assign the 'value' property
          // if it's truthy; otherwise, assign the 'default' property
          newObject[key] = newObject[key].value || newObject[key].default;
        }
      }
    }
    return newObject;
  };

  // get all brand ids except the current brand id
  const allBrandIds = useMemo(() => {
    return brands
      .filter((brand) => {
        return brand.id.value !== brandId;
      })
      .map((brand) => {
        return brand.id.value;
      });
  }, [brands, brandId]);

  const handleSaveBrand = useCallback(async () => {
    // Ensure all settings are synchronized before saving
    const isReadingTimeEnabled = brandFeatures.includes("readingtime");
    
    // Get the final values for mainContent and excludeContent (could come from either aiSettings or readingTimeSettings)
    // Since they should be synchronized, it doesn't matter which one we use
    const finalMainContent = readingTimeSettings.mainContent?.value || aiSettings.mainContent?.value || "";
    const finalExcludeContent = readingTimeSettings.excludeContent?.value || aiSettings.excludeContent?.value || "";
    
    // Create a copy of tentaclesSettings with synchronized readingtime settings
    const updatedTentaclesSettings = {
      ...tentaclesSettings,
      settings: {
        ...tentaclesSettings.settings,
        // Ensure collectReadingtime is synchronized with the feature status
        collectReadingtime: {
          ...tentaclesSettings.settings.collectReadingtime,
          value: isReadingTimeEnabled
        },
        // Use the final synchronized values
        mainContent: {
          ...tentaclesSettings.settings.mainContent,
          value: finalMainContent
        },
        excludeContent: {
          ...tentaclesSettings.settings.excludeContent,
          value: finalExcludeContent
        }
      }
    };

    // Make sure readingTimeSettings and aiSettings are also synchronized with the final values
    const updatedReadingTimeSettings = {
      ...readingTimeSettings,
      mainContent: {
        ...readingTimeSettings.mainContent,
        value: finalMainContent
      },
      excludeContent: {
        ...readingTimeSettings.excludeContent,
        value: finalExcludeContent
      }
    };
    
    const updatedAiSettings = {
      ...aiSettings,
      mainContent: {
        ...aiSettings.mainContent,
        value: finalMainContent
      },
      excludeContent: {
        ...aiSettings.excludeContent,
        value: finalExcludeContent
      }
    };
    
    const dataToSave = {
      active: brandActive,
      aiSettings: mapValuesToBaseAttr(updatedAiSettings),
      apiToken: brand.apiToken.value || brand.apiToken.default,
      brandlogo: brandLogoSrc,
      features: brandFeatures,
      insightsSettings: mapValuesToBaseAttr(insightsSettings),
      name: brandName,
      primaryStoryType: primaryStoryType,
      readingDirection: readingDirection,
      readingTimeSettings: mapValuesToBaseAttr(updatedReadingTimeSettings),
      storiesSettings: mapValuesToBaseAttr(storiesSettings),
      statisticsSettings: {
        atInternetAuth: mapValuesToBaseAttr(statisticsSettings.atInternetAuth),
        gaAuth: mapValuesToBaseAttr(statisticsSettings.gaAuth),
      },
      tentaclesSettings: {
        abTestSettings: mapValuesToBaseAttr(updatedTentaclesSettings.abTestSettings),
        classifications: mapValuesToBaseAttr(updatedTentaclesSettings.classifications),
        settings: mapValuesToBaseAttr(updatedTentaclesSettings.settings),
        tentaclesInfo: mapValuesToBaseAttr(updatedTentaclesSettings.tentaclesInfo),
      },
      timezone: timezone,
      trackingDays: trackingDays,
      videosSettings: mapValuesToBaseAttr(videosSettings),
      wavesSettings: mapValuesToBaseAttr(wavesSettings),
      customMetricsSettings: customMetricsSettings,
    };
  
    try {
      if (newBrand) {
        dataToSave["id"] = brandId;
        await dispatch(createBrand(dataToSave));
      } else {
        await dispatch(updateBrand(brandId, dataToSave));
      }
      setHasAttemptedUpdate(true);
    } catch (err) {
      console.log(err);
    }
  }, [
    brandActive,
    aiSettings,
    brand,
    brandFeatures,
    brandId,
    brandLogoSrc,
    brandName,
    dispatch,
    insightsSettings,
    primaryStoryType,
    readingDirection,
    readingTimeSettings,
    storiesSettings,
    statisticsSettings,
    tentaclesSettings,
    timezone,
    trackingDays,
    videosSettings,
    wavesSettings,
    customMetricsSettings,
    newBrand
  ]);

  useEffect(() => {
    if (hasAttemptedUpdate && (!updatingBrand && !creatingBrand)) {
      if (updatingBrandError === null && creatingBrandError === null) {
        closeModal();
      }
    }
  }, [hasAttemptedUpdate, creatingBrand, creatingBrandError, updatingBrand, updatingBrandError, closeModal]);

  useEffect(() => {
    // Reset the brand update error state when the modal is opened
    dispatch(resetBrandStates());
  }, [dispatch]);

  return (
    <div className="edit-brand-modal-wrapper">
      <Modal
        isOpen={subModalOpen}
        onClose={() => {
          closeSubModal();
        }}
      >
        {currentSubModal}
      </Modal>

      <h1 className="modal-title">{brand?.id?.value ? "Edit" : "Add"} Brand</h1>

      <div className="edit-brand-container">
        <div className="edit-brand-details">
          <div className="brand-active-switch-container">
            <span className="toggle-switch-label"> Brand Active</span>
            <ToggleSwitch
              checked={brandActive}
              onChange={(e) => {
                setBrandActive(e);
              }}
            />
          </div>
          <Input
            id="id"
            label="Brand Id"
            value={brandId}
            separateLines
            fullWidth
            disabled={!newBrand}
            onChange={(e) => {
              if (!newBrand) return false;
              const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
              if (value) {
                setBrandId(value.toLowerCase());
              } else {
                setBrandId("");
              }
            }}
          />
          {!newBrand && (
            <Input
              id="token"
              label="API token"
              value={brand?.apiToken?.value || brand?.apiToken?.default}
              onChange={() => {}}
              separateLines
              fullWidth
              readOnly
              disabled
            />
          )}
          <Input
            id="name"
            label="Brand name"
            value={brandName}
            onChange={(e) => {
              setBrandName(e.target.value);
            }}
            separateLines
            fullWidth
          />

          <div className="drop-select">
            <label htmlFor="timezone">Timezone</label>
            <Select
              search
              fullWidth
              options={timezones}
              selectedOption={timezone}
              setSelectedOption={(e) => {
                setTimezone(e);
              }}
              styling={{
                selectOptionsContainerClassName: "timezone-options-dropdown",
              }}
            />
          </div>
          <Input
            id="readingDirection"
            label="Reading Direction"
            value={readingDirection === "ltr" ? "Left to right" : "Right to left"}
            onChange={() => {}}
            separateLines
            fullWidth
            readOnly
            disabled
          />
          <Input
            id="trackingDays"
            type="number"
            label="Tracking Days"
            value={trackingDays}
            onChange={(e) => {
              setTrackingDays(e.target.valueAsNumber);
            }}
            separateLines
            fullWidth
          />
          <Input
            id="primaryStoryType"
            label="Primary story type"
            value={primaryStoryType}
            onChange={(e) => {
              setPrimaryStoryType(e);
            }}
            separateLines
            fullWidth
          />
        </div>
        <div className="brand-features">
          <h3>Features</h3>
          <div className="brand-features-container">
            <ul className="features-list">
              {FEATURES.map((feature) => {
                return (
                  <li className="feature-item" key={feature.name}>
                    <div className="feature-name">{feature.name.toLocaleLowerCase()}</div>
                    <div className="feature-toggle">
                      <ToggleSwitch
                        checked={brandFeatures.includes(feature.name)}
                        disabled={false}
                        onChange={() => {
                          handleFeatureSwitch(feature.name);
                        }}
                      />
                    </div>
                    <div className="feature-settings-icon">
                      {feature.hasSettings ? (
                        // @ts-ignore
                        <SettingsIcon
                          color={brandFeatures.includes(feature.name) ? "#1abcae" : "gray"}
                          width="30"
                          onClick={() => {
                            if (!brandFeatures.includes(feature.name)) return false;
                            navigate(`/admin/brands/${brandId}/${feature.name}`);
                          }}
                        />
                      ) : (
                        <div className="height-corrector"></div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        <div className="other-settings">
          <div className="brand-logo-wrapper">
            <div className="brand-logo-container">
              <img src={brandLogoSrc} alt={`${brandName} logo`} title={`Logo for ${brandName}`} />
            </div>
            <div className="select-logo">
              <input type="file" id="brand-logo" onChange={handleFileUpdate} />

              <label htmlFor="brand-logo">Select new logo</label>
            </div>
          </div>

          <StatisticsSettings
            showStatisticsSettings={showStatisticsSettings}
            handleToggleStatistics={handleToggleStatistics}
            onInputChange={handleEditStaticsSettings}
            data={statisticsSettings}
          />
          <CopyConfiguration
            showCopyConfiguration={showCopyConfiguration}
            handleToggleCopyConfiguration={handleToggleCopyConfiguration}
            allBrandIds={allBrandIds}
          />
        </div>
      </div>
      <div className="sub-modal-error">
        {updatingBrandError && (
          <div className="error-message">
            <strong>Failed updating brand:</strong> <code>{updatingBrandError.message}</code>
          </div>
        )}
        {creatingBrandError && (
          <div className="error-message">
            <strong>Failed creating brand:</strong> <code>{creatingBrandError.message}</code>
          </div>
        )}
      </div>
      <div className="sub-modal-actions">
        <Button variant="danger" onClick={closeModal}>
          Cancel
        </Button>
        <Button.Feedback
          variant="success"
          onClick={() => {
            handleSaveBrand();
          }}
          disabled={brandName === "" || brandId === ""}
        >
          OK
        </Button.Feedback>
      </div>
    </div>
  );
};

export default EditBrandModal;
